import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Divider,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  CircularProgress,
  Stack
} from '@mui/material';
import {
  Security as SecurityIcon,
  Password as PasswordIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Fingerprint as FingerprintIcon,
  VpnKey as KeyIcon,
  Block as BlockIcon,
  Public as PublicIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  Computer as DeviceIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  DeleteForever as DeleteIcon
} from '@mui/icons-material';
import { useNotify } from '../../ui/NotificationSystem';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';

interface SecurityDashboard {
  overview: {
    total_logins: number;
    failed_logins: number;
    blocked_attempts: number;
    active_sessions: number;
    locked_accounts: number;
    mfa_enabled_users: number;
    high_risk_events: number;
    password_changes: number;
  };
  recent_events: SecurityEvent[];
  failed_logins: {
    total: number;
    unique_ips: number;
    top_ips: Record<string, number>;
  };
  active_sessions: {
    total: number;
    by_role: Record<string, number>;
    unique_users: number;
  };
  alerts: SecurityAlert[];
  trends: {
    successful_logins: Record<string, number>;
    failed_logins: Record<string, number>;
  };
}

interface SecurityEvent {
  id: number;
  event_type: string;
  user: string;
  ip_address: string;
  risk_level: 'low' | 'medium' | 'high';
  occurred_at: string;
}

interface SecurityAlert {
  type: 'info' | 'warning' | 'danger';
  title: string;
  message: string;
  severity: 'low' | 'medium' | 'high';
}

interface ActiveSession {
  id: string;
  user: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
  ip_address: string;
  user_agent: string;
  last_activity: string;
  expires_at: string;
}

interface PasswordPolicy {
  min_length: number;
  require_uppercase: boolean;
  require_lowercase: boolean;
  require_numbers: boolean;
  require_special_chars: boolean;
  password_expiry_days: number;
  prevent_reuse_count: number;
}

interface SecuritySettings {
  password_policy: PasswordPolicy;
  mfa_required: boolean;
  session_timeout_minutes: number;
  max_failed_attempts: number;
  lockout_duration_minutes: number;
  ip_whitelist_enabled: boolean;
  ip_whitelist: string[];
  ip_blacklist: string[];
}

const SecurityPage: React.FC = () => {
  const { success: showSuccess, error: showError } = useNotify();
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState<SecurityDashboard | null>(null);
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);
  const [settings, setSettings] = useState<SecuritySettings>({
    password_policy: {
      min_length: 8,
      require_uppercase: true,
      require_lowercase: true,
      require_numbers: true,
      require_special_chars: true,
      password_expiry_days: 90,
      prevent_reuse_count: 5
    },
    mfa_required: false,
    session_timeout_minutes: 120,
    max_failed_attempts: 5,
    lockout_duration_minutes: 30,
    ip_whitelist_enabled: false,
    ip_whitelist: [],
    ip_blacklist: []
  });

  const [ipDialogOpen, setIpDialogOpen] = useState(false);
  const [ipDialogType, setIpDialogType] = useState<'whitelist' | 'blacklist'>('whitelist');
  const [newIpAddress, setNewIpAddress] = useState('');

  useEffect(() => {
    loadSecurityData();
  }, []);

  const loadSecurityData = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const token = localStorage.getItem('auth_token');

      const [dashboardRes, sessionsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/security/dashboard?days=7`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        }),
        fetch(`${API_BASE_URL}/admin/security/active-sessions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        })
      ]);

      if (dashboardRes.ok) {
        const dashboardData = await dashboardRes.json();
        setDashboard(dashboardData.data);
      }

      if (sessionsRes.ok) {
        const sessionsData = await sessionsRes.json();
        setActiveSessions(sessionsData.data.sessions || []);
      }

    } catch (error) {
      console.error('Error loading security data:', error);
      showError('Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${API_BASE_URL}/admin/security/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        showSuccess('Security settings saved successfully');
      } else {
        throw new Error('Failed to save settings');
      }

    } catch (error) {
      console.error('Error saving settings:', error);
      showError('Failed to save security settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddIpAddress = () => {
    if (!newIpAddress.trim()) return;

    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    if (!ipRegex.test(newIpAddress)) {
      showError('Invalid IP address format');
      return;
    }

    if (ipDialogType === 'whitelist') {
      setSettings(prev => ({
        ...prev,
        ip_whitelist: [...prev.ip_whitelist, newIpAddress]
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        ip_blacklist: [...prev.ip_blacklist, newIpAddress]
      }));
    }

    setNewIpAddress('');
    setIpDialogOpen(false);
  };

  const handleRemoveIpAddress = (ip: string, type: 'whitelist' | 'blacklist') => {
    if (type === 'whitelist') {
      setSettings(prev => ({
        ...prev,
        ip_whitelist: prev.ip_whitelist.filter(i => i !== ip)
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        ip_blacklist: prev.ip_blacklist.filter(i => i !== ip)
      }));
    }
  };

  const handleTerminateSession = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to terminate this session?')) return;

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${API_BASE_URL}/admin/security/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        showSuccess('Session terminated successfully');
        await loadSecurityData();
      } else {
        throw new Error('Failed to terminate session');
      }

    } catch (error) {
      console.error('Error terminating session:', error);
      showError('Failed to terminate session');
    }
  };

  const getRiskColor = (level: string): 'error' | 'warning' | 'success' | 'default' => {
    switch (level) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getAlertSeverity = (type: string): 'error' | 'warning' | 'info' | 'success' => {
    switch (type) {
      case 'danger': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'info';
    }
  };

  const prepareChartData = () => {
    if (!dashboard?.trends) return [];

    const dates = Object.keys(dashboard.trends.successful_logins).sort();
    
    return dates.map(date => ({
      date,
      successful: dashboard.trends.successful_logins[date] || 0,
      failed: dashboard.trends.failed_logins[date] || 0
    }));
  };

  const StatCard = ({ icon, title, value, color }: { icon: React.ReactNode; title: string; value: number | string; color?: string }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" style={{ color }}>
              {value}
            </Typography>
          </Box>
          <Box color={color || 'action.active'}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderDashboardTab = () => (
    <Box>
      {/* Security Alerts */}
      {dashboard?.alerts && dashboard.alerts.length > 0 && (
        <Stack spacing={1} sx={{ mb: 3 }}>
          {dashboard.alerts.map((alert, index) => (
            <Alert 
              key={index} 
              severity={getAlertSeverity(alert.type)}
              icon={alert.severity === 'high' ? <WarningIcon /> : undefined}
            >
              <Typography variant="subtitle2">{alert.title}</Typography>
              <Typography variant="body2">{alert.message}</Typography>
            </Alert>
          ))}
        </Stack>
      )}

      {/* Overview Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3, mb: 3 }}>
        <StatCard 
          icon={<CheckIcon sx={{ fontSize: 40 }} />}
          title="Total Logins (7d)"
          value={dashboard?.overview.total_logins || 0}
          color="#4caf50"
        />
        <StatCard 
          icon={<ErrorIcon sx={{ fontSize: 40 }} />}
          title="Failed Logins (7d)"
          value={dashboard?.overview.failed_logins || 0}
          color="#f44336"
        />
        <StatCard 
          icon={<DeviceIcon sx={{ fontSize: 40 }} />}
          title="Active Sessions"
          value={dashboard?.overview.active_sessions || 0}
          color="#2196f3"
        />
        <StatCard 
          icon={<BlockIcon sx={{ fontSize: 40 }} />}
          title="Locked Accounts"
          value={dashboard?.overview.locked_accounts || 0}
          color="#ff9800"
        />
        <StatCard 
          icon={<FingerprintIcon sx={{ fontSize: 40 }} />}
          title="MFA Enabled Users"
          value={dashboard?.overview.mfa_enabled_users || 0}
          color="#4caf50"
        />
        <StatCard 
          icon={<WarningIcon sx={{ fontSize: 40 }} />}
          title="High Risk Events (7d)"
          value={dashboard?.overview.high_risk_events || 0}
          color="#f44336"
        />
        <StatCard 
          icon={<BlockIcon sx={{ fontSize: 40 }} />}
          title="Blocked Attempts (7d)"
          value={dashboard?.overview.blocked_attempts || 0}
          color="#ff9800"
        />
        <StatCard 
          icon={<KeyIcon sx={{ fontSize: 40 }} />}
          title="Password Changes (7d)"
          value={dashboard?.overview.password_changes || 0}
          color="#2196f3"
        />
      </Box>

      {/* Login Trends Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Login Activity Trends (Last 7 Days)
          </Typography>
          <Box sx={{ height: 300, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={prepareChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Line type="monotone" dataKey="successful" stroke="#4caf50" name="Successful Logins" />
                <Line type="monotone" dataKey="failed" stroke="#f44336" name="Failed Logins" />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Recent Security Events */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">
              Recent Security Events
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadSecurityData}
              size="small"
            >
              Refresh
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Event Type</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Risk Level</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboard?.recent_events?.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      <Chip 
                        label={event.event_type.replace('_', ' ').toUpperCase()} 
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{event.user}</TableCell>
                    <TableCell>{event.ip_address}</TableCell>
                    <TableCell>
                      <Chip 
                        label={event.risk_level} 
                        color={getRiskColor(event.risk_level)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(event.occurred_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  const renderPasswordPolicyTab = () => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
          <PasswordIcon color="primary" />
          <Typography variant="h6">Password Policy Configuration</Typography>
        </Box>

        <Stack spacing={3}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}>
            <TextField
              fullWidth
              type="number"
              label="Minimum Password Length"
              value={settings.password_policy.min_length}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                password_policy: {
                  ...prev.password_policy,
                  min_length: parseInt(e.target.value) || 8
                }
              }))}
              InputProps={{ inputProps: { min: 6, max: 32 } }}
              helperText="Minimum number of characters required"
            />

            <TextField
              fullWidth
              type="number"
              label="Password Expiry (Days)"
              value={settings.password_policy.password_expiry_days}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                password_policy: {
                  ...prev.password_policy,
                  password_expiry_days: parseInt(e.target.value) || 90
                }
              }))}
              InputProps={{ inputProps: { min: 0, max: 365 } }}
              helperText="0 = never expires"
            />

            <TextField
              fullWidth
              type="number"
              label="Prevent Password Reuse"
              value={settings.password_policy.prevent_reuse_count}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                password_policy: {
                  ...prev.password_policy,
                  prevent_reuse_count: parseInt(e.target.value) || 5
                }
              }))}
              InputProps={{ inputProps: { min: 0, max: 24 } }}
              helperText="Number of previous passwords to prevent reuse"
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Password Complexity Requirements
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.password_policy.require_uppercase}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      password_policy: {
                        ...prev.password_policy,
                        require_uppercase: e.target.checked
                      }
                    }))}
                  />
                }
                label="Require Uppercase (A-Z)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.password_policy.require_lowercase}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      password_policy: {
                        ...prev.password_policy,
                        require_lowercase: e.target.checked
                      }
                    }))}
                  />
                }
                label="Require Lowercase (a-z)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.password_policy.require_numbers}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      password_policy: {
                        ...prev.password_policy,
                        require_numbers: e.target.checked
                      }
                    }))}
                  />
                }
                label="Require Numbers (0-9)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.password_policy.require_special_chars}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      password_policy: {
                        ...prev.password_policy,
                        require_special_chars: e.target.checked
                      }
                    }))}
                  />
                }
                label="Require Special (!@#$%)"
              />
            </Box>
          </Box>

          <Alert severity="info" icon={<InfoIcon />}>
            Password policies will be enforced for all new password changes. Existing passwords 
            will need to be updated when users next change their password or when password expiry occurs.
          </Alert>

          <Box>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Password Policy'}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderSessionManagementTab = () => (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
            <DeviceIcon color="primary" />
            <Typography variant="h6">Session Configuration</Typography>
          </Box>

          <Stack spacing={3}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}>
              <TextField
                fullWidth
                type="number"
                label="Session Timeout (Minutes)"
                value={settings.session_timeout_minutes}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  session_timeout_minutes: parseInt(e.target.value) || 120
                }))}
                InputProps={{ inputProps: { min: 5, max: 1440 } }}
                helperText="Time until inactive sessions expire"
              />

              <TextField
                fullWidth
                type="number"
                label="Max Failed Login Attempts"
                value={settings.max_failed_attempts}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  max_failed_attempts: parseInt(e.target.value) || 5
                }))}
                InputProps={{ inputProps: { min: 3, max: 10 } }}
                helperText="Failed attempts before account lockout"
              />

              <TextField
                fullWidth
                type="number"
                label="Lockout Duration (Minutes)"
                value={settings.lockout_duration_minutes}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  lockout_duration_minutes: parseInt(e.target.value) || 30
                }))}
                InputProps={{ inputProps: { min: 5, max: 1440 } }}
                helperText="How long accounts remain locked"
              />
            </Box>

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={saving}
              sx={{ width: 'fit-content' }}
            >
              {saving ? 'Saving...' : 'Save Session Settings'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Active Sessions */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <DeviceIcon color="primary" />
              <Typography variant="h6">Active Sessions</Typography>
              <Chip label={activeSessions.length} color="primary" size="small" />
            </Box>
            <Button startIcon={<RefreshIcon />} onClick={loadSecurityData} size="small">
              Refresh
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>Last Activity</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeSessions.map((session) => (
                  <TableRow key={session.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{session.user.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {session.user.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={session.user.role} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationIcon fontSize="small" color="action" />
                        {session.ip_address}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={session.user_agent}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <DeviceIcon fontSize="small" color="action" />
                          <Typography variant="caption">
                            {session.user_agent.split(' ')[0]}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <TimeIcon fontSize="small" color="action" />
                        {new Date(session.last_activity).toLocaleString()}
                      </Box>
                    </TableCell>
                    <TableCell>{new Date(session.expires_at).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Terminate Session">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleTerminateSession(session.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {activeSessions.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">
                No active sessions found
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  );

  const render2FATab = () => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
          <FingerprintIcon color="primary" />
          <Typography variant="h6">Two-Factor Authentication (2FA)</Typography>
        </Box>

        <Stack spacing={3}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.mfa_required}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  mfa_required: e.target.checked
                }))}
              />
            }
            label="Require 2FA for all users"
          />
          <Typography variant="body2" color="text.secondary">
            When enabled, all users will be required to set up two-factor authentication
            on their next login.
          </Typography>

          <Alert severity="warning">
            <Typography variant="subtitle2">Important</Typography>
            <Typography variant="body2">
              Enabling mandatory 2FA will affect all users. Make sure to communicate this change 
              to your team before enabling. Users will need a compatible authenticator app 
              (Google Authenticator, Authy, etc.) to set up 2FA.
            </Typography>
          </Alert>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              2FA Statistics
            </Typography>
            <Box display="flex" justifyContent="space-between" sx={{ mt: 2 }}>
              <Typography variant="body2">Users with 2FA Enabled:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {dashboard?.overview.mfa_enabled_users || 0}
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            disabled={saving}
            sx={{ width: 'fit-content' }}
          >
            {saving ? 'Saving...' : 'Save 2FA Settings'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderIPManagementTab = () => (
    <Stack spacing={3}>
      {/* IP Whitelist */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <CheckIcon color="success" />
              <Typography variant="h6">IP Whitelist</Typography>
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.ip_whitelist_enabled}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      ip_whitelist_enabled: e.target.checked
                    }))}
                  />
                }
                label="Enable Whitelist"
              />
              <Button
                variant="outlined"
                startIcon={<PublicIcon />}
                onClick={() => {
                  setIpDialogType('whitelist');
                  setIpDialogOpen(true);
                }}
              >
                Add IP
              </Button>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Only allow access from whitelisted IP addresses. Warning: Enabling this may lock you out if your IP is not whitelisted.
          </Typography>

          {settings.ip_whitelist.length > 0 ? (
            <List>
              {settings.ip_whitelist.map((ip, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <PublicIcon />
                  </ListItemIcon>
                  <ListItemText primary={ip} />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleRemoveIpAddress(ip, 'whitelist')}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              No IP addresses in whitelist. Add trusted IP addresses to restrict access.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* IP Blacklist */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <BlockIcon color="error" />
              <Typography variant="h6">IP Blacklist</Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<BlockIcon />}
              onClick={() => {
                setIpDialogType('blacklist');
                setIpDialogOpen(true);
              }}
            >
              Block IP
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Block access from specific IP addresses. Blacklisted IPs will be denied access immediately.
          </Typography>

          {settings.ip_blacklist.length > 0 ? (
            <List>
              {settings.ip_blacklist.map((ip, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <BlockIcon color="error" />
                  </ListItemIcon>
                  <ListItemText primary={ip} />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleRemoveIpAddress(ip, 'blacklist')}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              No IP addresses in blacklist. Add malicious IP addresses to block them.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Top Failed Login IPs */}
      {dashboard?.failed_logins && Object.keys(dashboard.failed_logins.top_ips || {}).length > 0 && (
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
              <WarningIcon color="warning" />
              <Typography variant="h6">Top Failed Login IPs</Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These IP addresses have the most failed login attempts in the last 7 days.
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>IP Address</TableCell>
                    <TableCell align="right">Failed Attempts</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(dashboard.failed_logins.top_ips).map(([ip, count]) => (
                    <TableRow key={ip}>
                      <TableCell>{ip}</TableCell>
                      <TableCell align="right">
                        <Chip label={count} color="error" size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          startIcon={<BlockIcon />}
                          onClick={() => {
                            if (!settings.ip_blacklist.includes(ip)) {
                              setSettings(prev => ({
                                ...prev,
                                ip_blacklist: [...prev.ip_blacklist, ip]
                              }));
                            }
                          }}
                          disabled={settings.ip_blacklist.includes(ip)}
                        >
                          {settings.ip_blacklist.includes(ip) ? 'Blocked' : 'Block'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Button
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={handleSaveSettings}
        disabled={saving}
        sx={{ width: 'fit-content' }}
      >
        {saving ? 'Saving...' : 'Save IP Settings'}
      </Button>
    </Stack>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 1 }}>
          <ShieldIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Security Settings
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Manage system security policies, monitor security events, and configure authentication settings
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab icon={<SecurityIcon />} label="Dashboard" />
          <Tab icon={<PasswordIcon />} label="Password Policy" />
          <Tab icon={<DeviceIcon />} label="Sessions" />
          <Tab icon={<FingerprintIcon />} label="Two-Factor Auth" />
          <Tab icon={<PublicIcon />} label="IP Management" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && renderDashboardTab()}
      {activeTab === 1 && renderPasswordPolicyTab()}
      {activeTab === 2 && renderSessionManagementTab()}
      {activeTab === 3 && render2FATab()}
      {activeTab === 4 && renderIPManagementTab()}

      {/* IP Address Dialog */}
      <Dialog open={ipDialogOpen} onClose={() => setIpDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {ipDialogType === 'whitelist' ? 'Add IP to Whitelist' : 'Block IP Address'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="IP Address"
            placeholder="192.168.1.1 or 192.168.1.0/24"
            value={newIpAddress}
            onChange={(e) => setNewIpAddress(e.target.value)}
            helperText="Enter a single IP address or CIDR notation for ranges"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIpDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddIpAddress}
            color={ipDialogType === 'whitelist' ? 'primary' : 'error'}
          >
            {ipDialogType === 'whitelist' ? 'Add to Whitelist' : 'Block IP'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityPage;
